import React, { ErrorInfo } from "react";
import { ErrorBoundary, FallbackProps } from "react-error-boundary";
import ErrorPage from "../errorPage";

const RELOAD_KEY = "app_chunk_reload_count";
const MAX_RELOAD = 3;

/**
 * Detect cache/chunk related errors
 */
const isChunkError = (error: any) => {
    const msg = (error?.message || "").toLowerCase();

    return (
        msg.includes("chunkloaderror") ||
        msg.includes("loading chunk") ||
        msg.includes("failed to fetch dynamically imported module") ||
        msg.includes("importing a module script failed") ||
        msg.includes("unexpected token '<'")
    );
};



function ErrorFallback({
    error,
    resetErrorBoundary,
}: FallbackProps) {

    return (
        <ErrorPage
            error={error}
            resetErrorBoundary={resetErrorBoundary} />
    );
}

interface Props {
    children: React.ReactNode;
}

const GlobalErrorBoundary: React.FC<Props> = ({ children }) => {
    /**
     * Reset reload counter after successful render
     */
    React.useEffect(() => {
        sessionStorage.removeItem(RELOAD_KEY);
    }, []);

    const handleError = async (
        error: unknown,
        info: ErrorInfo
    ) => {
        console.error("Global Error", error);
        console.error(info.componentStack);

        /**
         * Send to backend
         */
        // try {
        //     await fetch("/api/log-error", {
        //         method: "POST",
        //         headers: {
        //             "Content-Type": "application/json",
        //         },
        //         body: JSON.stringify({
        //             message: error.message,
        //             stack: error.stack,
        //             componentStack: info.componentStack,
        //             url: window.location.href,
        //             userAgent: navigator.userAgent,
        //             time: new Date().toISOString(),
        //         }),
        //     });
        // } catch {}

        /**
         * Auto recover only for cache/chunk issues
         */
        if (isChunkError(error)) {
            const count = Number(
                sessionStorage.getItem(RELOAD_KEY) || "0"
            );

            if (count < MAX_RELOAD) {
                sessionStorage.setItem(
                    RELOAD_KEY,
                    String(count + 1)
                );

                console.warn(
                    `Chunk error detected. Reload attempt ${count + 1}/${MAX_RELOAD}`
                );

                /**
                 * Optional cache cleanup
                 */
                if ("caches" in window) {
                    try {
                        const keys = await caches.keys();
                        await Promise.all(
                            keys.map((k) => caches.delete(k))
                        );
                    } catch { }
                }

                window.location.reload();
                return;
            }

            console.error(
                "Maximum reload attempts reached."
            );
        }
    };

    return (
        <ErrorBoundary
            FallbackComponent={ErrorFallback}
            onError={handleError}
            onReset={() => { }}
        >
            {children}
        </ErrorBoundary>
    );
};

export default GlobalErrorBoundary;